import {
  ACCEL,
  BASE_MAX_SPEED,
  CAMERA_HEIGHT,
  COMBO_MAX,
  COMBO_WINDOW_MS,
  CENTRIFUGAL,
  CRASH_INVULN_MS,
  CRASH_SPEED_KEEP,
  CRASH_STUN_MS,
  DRAG,
  FLOW_MAX,
  FLOW_SEND_COST,
  FLOW_SEND_MS,
  GRAVITY,
  HOP_HOLD_BOOST,
  HOP_HOLD_MAX_S,
  HOP_VELOCITY,
  INPUT_BUFFER_MS,
  LANE_X,
  MAX_CRASHES_ENDLESS,
  MIN_SPEED,
  OFFROAD_DECEL,
  OFFROAD_MAX_SPEED,
  PLAYER_HALF_W,
  RAMP_VELOCITY,
  SCORE_AIR_PER_S,
  SCORE_GEAR,
  SCORE_NEAR_MISS,
  SCORE_ORB,
  SCORE_PER_METRE,
  SCORE_TRICK_BASE,
  SEGMENT_LENGTH,
  SEND_SPEED_MUL,
  START_Z,
  STEER_LOW_SPEED_FLOOR,
  STEER_RATE,
  TICK_MS,
  TICK_S,
  TUCK_DRAG,
  TUCK_SPEED_BONUS,
  UNITS_PER_METRE,
  UPRIGHT_DRAG,
} from './constants';
import { clamp, entityX, geyserActive, Track, type Entity } from './track';
import type { HighlightMoment, RunSummary, ThemeId } from './types';

export interface SimInput {
  /** -1 (hard left) .. 1 (hard right). */
  steer: number;
  hop: boolean;
  tuck: boolean;
  send: boolean;
  trick: boolean;
}

export const NEUTRAL_INPUT: SimInput = { steer: 0, hop: false, tuck: false, send: false, trick: false };

export type SimEvent =
  | { type: 'jump' }
  | { type: 'land'; hard: boolean }
  | { type: 'ramp' }
  | { type: 'orb'; combo: number }
  | { type: 'gear'; points: number }
  | { type: 'boost' }
  | { type: 'send' }
  | { type: 'sendEnd' }
  | { type: 'nearMiss'; points: number }
  | { type: 'trickStart'; name: string }
  | { type: 'trickLanded'; name: string; points: number; clean: boolean }
  | { type: 'trickSketchy' }
  | { type: 'crash'; cause: Entity['kind'] | 'offroad' }
  | { type: 'offroad'; on: boolean }
  | { type: 'comboBreak' }
  | { type: 'finish'; ms: number }
  | { type: 'dead' };

export const TRICKS = [
  { name: 'Whip', dur: 0.5, mul: 1 },
  { name: 'Tabletop', dur: 0.55, mul: 1.15 },
  { name: 'Suicide No-Hander', dur: 0.6, mul: 1.35 },
  { name: 'Backflip', dur: 0.72, mul: 1.7 },
  { name: 'Tailwhip 360', dur: 0.8, mul: 2.1 },
] as const;

export interface SimOptions {
  track: Track;
  /** Endless and daily runs end after three crashes; trails and races do not. */
  lives?: number;
}

export class Sim {
  readonly track: Track;
  readonly theme: ThemeId;
  readonly lives: number;

  // --- state ---
  timeMs = 0;
  z = START_Z;
  /** Normalised road-x, -1..1 is the rideable surface. */
  x = 0;
  /** Height above the road surface, world units. */
  y = 0;
  vy = 0;
  speed = MIN_SPEED;
  grounded = true;
  flow = 0;
  sendMs = 0;
  stunMs = 0;
  invulnMs = 0;
  crashes = 0;
  score = 0;
  combo = 0;
  comboMs = 0;
  bestCombo = 0;
  topSpeed = 0;
  airMs = 0;
  currentAirMs = 0;
  tricks = 0;
  nearMisses = 0;
  orbs = 0;
  gears = 0;
  offroad = false;
  finished = false;
  finishMs = 0;
  dead = false;
  started = false;

  /** Visual-only: body lean and in-air rotation, exposed for the renderer. */
  lean = 0;
  trickActive = false;
  trickIndex = 0;
  trickPhase = 0;

  private prevInput: SimInput = { ...NEUTRAL_INPUT };
  private lastSegIndex = 0;
  private hopHoldS = 0;
  private hopBufferMs = 0;
  private trickBufferMs = 0;
  private sendBufferMs = 0;
  readonly highlights: HighlightMoment[] = [];
  private bestAirMs = 0;

  constructor(opts: SimOptions) {
    this.track = opts.track;
    this.theme = opts.track.theme.id;
    this.lives = opts.lives ?? Infinity;
    this.speed = MIN_SPEED;
  }

  get maxSpeed(): number {
    return BASE_MAX_SPEED * this.track.theme.speedMul;
  }

  get multiplier(): number {
    return 1 + this.combo * 0.25;
  }

  get distanceMetres(): number {
    return Math.max(0, this.z - START_Z) / UNITS_PER_METRE;
  }

  get progress(): number {
    return this.track.finite ? clamp(this.z / this.track.worldLength, 0, 1) : 0;
  }

  get sending(): boolean {
    return this.sendMs > 0;
  }

  /** Bouncing down a staircase: costs speed and rattles the frame. */
  get onStairs(): boolean {
    return this.grounded && this.track.segmentAtZ(this.z).stairs;
  }

  get gravity(): number {
    return GRAVITY * this.track.theme.gravity;
  }

  /** One fixed tick. Returns everything the presentation layer needs to react to. */
  step(input: SimInput): SimEvent[] {
    const ev: SimEvent[] = [];
    if (this.dead || this.finished) return ev;

    const dt = TICK_S;
    this.timeMs += TICK_MS;
    this.started = true;

    const stunned = this.stunMs > 0;
    if (stunned) this.stunMs = Math.max(0, this.stunMs - TICK_MS);
    if (this.invulnMs > 0) this.invulnMs = Math.max(0, this.invulnMs - TICK_MS);

    const eff: SimInput = stunned ? NEUTRAL_INPUT : input;
    const timeS = this.timeMs / 1000;

    this.updateBuffers(eff);
    this.updateSend(eff, ev);
    this.updateVertical(eff, ev, dt);
    this.updateSpeed(eff, dt);
    this.updateSteering(eff, dt);
    this.updateTrick(eff, ev, dt);

    const prevZ = this.z;
    this.z += this.speed * dt;
    this.track.ensure(Math.floor(this.z / SEGMENT_LENGTH) + 260);

    this.scoreDistance(prevZ);
    this.resolveEntities(prevZ, timeS, ev);
    this.updateCombo(ev);

    if (this.currentAirMs > this.bestAirMs) this.bestAirMs = this.currentAirMs;
    if (this.speed > this.topSpeed) this.topSpeed = this.speed;
    if (this.combo > this.bestCombo) this.bestCombo = this.combo;

    if (this.track.finite && this.z >= this.track.worldLength) {
      this.z = this.track.worldLength;
      this.finished = true;
      this.finishMs = this.timeMs;
      this.pushHighlight('finish', this.timeMs);
      ev.push({ type: 'finish', ms: this.timeMs });
    }

    const segIndex = Math.floor(this.z / SEGMENT_LENGTH);
    if (segIndex - this.lastSegIndex > 400) this.track.prune(segIndex);
    this.lastSegIndex = segIndex;

    this.prevInput = { ...input };
    return ev;
  }

  // --- subsystems -------------------------------------------------------

  /**
   * Edge-detect each action into a short memory. A player holding the button
   * through a landing, or pressing just before a ramp, still gets the action.
   */
  private updateBuffers(input: SimInput): void {
    this.hopBufferMs = Math.max(0, this.hopBufferMs - TICK_MS);
    this.trickBufferMs = Math.max(0, this.trickBufferMs - TICK_MS);
    this.sendBufferMs = Math.max(0, this.sendBufferMs - TICK_MS);
    if (input.hop && !this.prevInput.hop) this.hopBufferMs = INPUT_BUFFER_MS;
    if (input.trick && !this.prevInput.trick) this.trickBufferMs = INPUT_BUFFER_MS;
    if (input.send && !this.prevInput.send) this.sendBufferMs = INPUT_BUFFER_MS;
  }

  private updateSend(input: SimInput, ev: SimEvent[]): void {
    if (this.sendMs > 0) {
      this.sendMs = Math.max(0, this.sendMs - TICK_MS);
      if (this.sendMs === 0) ev.push({ type: 'sendEnd' });
    }
    if (this.sendBufferMs > 0 && this.flow >= FLOW_SEND_COST && this.sendMs === 0) {
      this.sendBufferMs = 0;
      this.flow -= FLOW_SEND_COST;
      this.sendMs = FLOW_SEND_MS;
      this.pushHighlight('send', this.timeMs);
      ev.push({ type: 'send' });
    }
  }

  private updateVertical(input: SimInput, ev: SimEvent[], dt: number): void {
    if (this.grounded) {
      if (this.hopBufferMs > 0) {
        this.hopBufferMs = 0;
        this.vy = HOP_VELOCITY;
        this.grounded = false;
        this.hopHoldS = 0;
        this.currentAirMs = 0;
        ev.push({ type: 'jump' });
      }
    } else {
      // Holding the hop input early in the jump adds lift, so timing rewards you.
      if (input.hop && this.vy > 0 && this.hopHoldS < HOP_HOLD_MAX_S) {
        this.vy += HOP_HOLD_BOOST * dt;
        this.hopHoldS += dt;
      }
      // Tucking in the air is a fast-fall; it is how you get back down before a gate.
      const extra = input.tuck ? this.gravity * 1.1 : 0;
      this.vy -= (this.gravity + extra) * dt;
      this.y += this.vy * dt;
      this.currentAirMs += TICK_MS;
      this.airMs += TICK_MS;

      if (this.y <= 0) {
        this.y = 0;
        const hard = this.vy < -this.gravity * 0.42;
        this.vy = 0;
        this.grounded = true;
        this.awardAir(ev);
        if (hard) this.speed *= 0.93;
        ev.push({ type: 'land', hard });
        this.currentAirMs = 0;
      }
    }
  }

  private awardAir(ev: SimEvent[]): void {
    const seconds = this.currentAirMs / 1000;
    if (seconds < 0.25) return;
    const points = Math.round(SCORE_AIR_PER_S * seconds * this.multiplier);
    this.score += points;
    this.flow = Math.min(FLOW_MAX, this.flow + seconds * 9);
    if (seconds >= 1.1) {
      this.bumpCombo();
      this.pushHighlight('bigair', this.timeMs, Math.round(this.currentAirMs));
    }
    void ev;
  }

  private updateSpeed(input: SimInput, dt: number): void {
    const theme = this.track.theme;
    const grade = this.track.gradeAt(this.z);
    const tucking = input.tuck && this.grounded;

    let ceiling = this.maxSpeed * (tucking ? TUCK_SPEED_BONUS : 1);
    if (this.sending) ceiling *= SEND_SPEED_MUL;

    if (this.offroad) {
      this.speed -= OFFROAD_DECEL * dt;
      if (this.speed < OFFROAD_MAX_SPEED) this.speed = Math.max(MIN_SPEED, this.speed);
    } else if (this.stunMs > 0) {
      this.speed += (MIN_SPEED * 1.5 - this.speed) * 2.4 * dt;
    } else {
      // Gravity down the fall line does the work; grade shapes how much.
      const gain = ACCEL * theme.gravity * clamp(0.55 + grade * 6, -0.4, 1.5);
      const dragK = (tucking ? TUCK_DRAG : input.hop || !this.grounded ? DRAG : UPRIGHT_DRAG) * 1e-4;
      const drop = dragK * this.speed * (this.speed / ceiling);
      this.speed += (gain - drop * 60) * dt;
      if (this.sending) this.speed += ACCEL * 0.8 * dt;
    }

    this.speed = clamp(this.speed, MIN_SPEED, ceiling * 1.02);

    // Stairs are for jumping, not for riding down.
    if (this.grounded && this.track.segmentAtZ(this.z).stairs) {
      this.speed = Math.max(MIN_SPEED, this.speed - 1500 * dt);
    }
  }

  private updateSteering(input: SimInput, dt: number): void {
    const seg = this.track.segmentAtZ(this.z);
    const speedFrac = clamp(this.speed / this.maxSpeed, 0, 1.2);
    const authority = Math.max(STEER_LOW_SPEED_FLOOR, Math.min(1, speedFrac * 2.4));
    const grip = this.grounded ? seg.grip * this.track.theme.grip : 0.72;

    let steer = clamp(input.steer, -1, 1);
    this.x += steer * STEER_RATE * authority * grip * dt;

    // Lane magnetism: with the thumb parked, the bike settles into a lane by itself.
    if (Math.abs(steer) < 0.14 && this.grounded && this.stunMs === 0) {
      const nearest = nearestLaneX(this.x);
      const delta = nearest - this.x;
      this.x += clamp(delta, -1, 1) * Math.min(1, Math.abs(delta) * 7) * 3.2 * dt;
    }

    // Corners throw you to the outside; hold a line or pay for it.
    this.x -= seg.curve * CENTRIFUGAL * speedFrac * speedFrac * dt;

    this.x = clamp(this.x, -1.45, 1.45);
    const nowOffroad = Math.abs(this.x) > 1;
    this.offroad = nowOffroad;
    this.lean += (steer * 0.85 - this.lean) * Math.min(1, 9 * dt);
  }

  private updateTrick(input: SimInput, ev: SimEvent[], dt: number): void {
    if (this.grounded) {
      if (this.trickActive) {
        // Landed mid-rotation. Forgiving: mostly-complete still counts.
        if (this.trickPhase >= 0.6) {
          this.landTrick(ev, false);
        } else {
          this.trickActive = false;
          this.trickPhase = 0;
          this.speed *= 0.88;
          this.breakCombo(ev);
          ev.push({ type: 'trickSketchy' });
        }
      }
      return;
    }
    if (this.trickBufferMs > 0 && !this.trickActive) {
      // Only offer a trick the remaining hang time can actually finish.
      const left = this.remainingAirS();
      if (left >= TRICKS[0]!.dur * 0.95) {
        this.trickBufferMs = 0;
        let idx = 0;
        for (let k = TRICKS.length - 1; k >= 0; k--) {
          if (TRICKS[k]!.dur <= left * 0.95) {
            idx = k;
            break;
          }
        }
        this.trickIndex = idx;
        this.trickActive = true;
        this.trickPhase = 0;
        ev.push({ type: 'trickStart', name: TRICKS[this.trickIndex]!.name });
      }
    }
    if (this.trickActive) {
      this.trickPhase += dt / TRICKS[this.trickIndex]!.dur;
      if (this.trickPhase >= 1) this.landTrick(ev, true);
    }
    void input;
  }

  private landTrick(ev: SimEvent[], clean: boolean): void {
    const t = TRICKS[this.trickIndex]!;
    const points = Math.round(SCORE_TRICK_BASE * t.mul * this.multiplier * (clean ? 1 : 0.6));
    this.score += points;
    this.tricks++;
    this.flow = Math.min(FLOW_MAX, this.flow + (clean ? 14 : 7));
    this.trickActive = false;
    this.trickPhase = 0;
    this.bumpCombo();
    ev.push({ type: 'trickLanded', name: t.name, points, clean });
  }

  private scoreDistance(prevZ: number): void {
    if (this.offroad) return;
    const metres = (this.z - prevZ) / UNITS_PER_METRE;
    this.score += metres * SCORE_PER_METRE * (1 + this.combo * 0.08) * (this.sending ? 2 : 1);
  }

  private updateCombo(ev: SimEvent[]): void {
    if (this.combo <= 0) return;
    this.comboMs -= TICK_MS;
    if (this.comboMs <= 0) {
      this.combo--;
      this.comboMs = COMBO_WINDOW_MS * 0.6;
      if (this.combo === 0) ev.push({ type: 'comboBreak' });
    }
  }

  private bumpCombo(): void {
    const before = this.combo;
    this.combo = Math.min(COMBO_MAX, this.combo + 1);
    this.comboMs = COMBO_WINDOW_MS;
    if (this.combo >= 6 && before < 6) this.pushHighlight('combo', this.timeMs, this.combo);
  }

  private breakCombo(ev: SimEvent[]): void {
    if (this.combo > 0) ev.push({ type: 'comboBreak' });
    this.combo = 0;
    this.comboMs = 0;
  }

  private resolveEntities(prevZ: number, timeS: number, ev: SimEvent[]): void {
    const from = Math.floor(prevZ / SEGMENT_LENGTH);
    const to = Math.floor(this.z / SEGMENT_LENGTH);
    for (let i = from; i <= to; i++) {
      const seg = this.track.segment(i);
      for (const e of seg.entities) {
        if (e.taken) continue;
        const ex = entityX(e, timeS);
        const dx = Math.abs(this.x - ex);
        const reach = PLAYER_HALF_W + e.hw;

        switch (e.kind) {
          case 'orb': {
            if (dx < reach && this.y < 420) {
              e.taken = true;
              this.orbs++;
              this.flow = Math.min(FLOW_MAX, this.flow + 7);
              this.score += SCORE_ORB * this.multiplier;
              this.bumpCombo();
              ev.push({ type: 'orb', combo: this.combo });
            }
            break;
          }
          case 'gear': {
            if (dx < reach && this.y >= e.clearY && this.y <= e.ceilY) {
              e.taken = true;
              this.gears++;
              const pts = Math.round(SCORE_GEAR * this.multiplier);
              this.score += pts;
              this.flow = FLOW_MAX;
              this.bumpCombo();
              this.pushHighlight('gear', this.timeMs, pts);
              ev.push({ type: 'gear', points: pts });
            }
            break;
          }
          case 'boost': {
            if (dx < reach && this.y < 160) {
              e.taken = true;
              this.speed = Math.min(this.maxSpeed * 1.45, this.speed * 1.3 + 1200);
              this.flow = Math.min(FLOW_MAX, this.flow + 18);
              ev.push({ type: 'boost' });
            }
            break;
          }
          case 'ramp': {
            if (dx < reach && this.grounded) {
              e.taken = true;
              // Capped at the base ceiling: without this, a sending rider flies
              // far enough to overshoot the clear run-out the generator reserved.
              const ratio = Math.min(1, this.speed / this.maxSpeed);
              this.vy = RAMP_VELOCITY * ratio * 0.55 + RAMP_VELOCITY * 0.5;
              this.grounded = false;
              this.currentAirMs = 0;
              this.hopHoldS = HOP_HOLD_MAX_S;
              ev.push({ type: 'ramp' });
            }
            break;
          }
          default: {
            if (e.kind === 'geyser' && !geyserActive(e, timeS)) break;
            if (dx < reach) {
              e.taken = true;
              if (this.hits(e)) {
                this.crash(e.kind, ev);
              } else if (e.kind !== 'pit') {
                // Cleared it vertically. Worth a little flow, but not a combo:
                // a gap spans many segments and would otherwise pay out per segment.
                this.flow = Math.min(FLOW_MAX, this.flow + 3);
              }
            } else if (dx < reach * 2.1 && this.invulnMs === 0 && e.kind !== 'pit') {
              e.taken = true;
              const pts = Math.round(SCORE_NEAR_MISS * this.multiplier);
              this.score += pts;
              this.flow = Math.min(FLOW_MAX, this.flow + 6);
              this.nearMisses++;
              this.bumpCombo();
              if (dx < reach * 1.4) this.pushHighlight('nearmiss', this.timeMs, Math.round(dx * 1000));
              ev.push({ type: 'nearMiss', points: pts });
            }
            break;
          }
        }
      }
    }
  }

  private hits(e: Entity): boolean {
    if (e.kind === 'gate') return this.y > e.ceilY;
    if (e.clearY > 0 && this.y >= e.clearY) return false;
    return true;
  }

  /** Seconds of hang time left before touchdown, from ballistics. */
  remainingAirS(): number {
    if (this.grounded) return 0;
    const g = this.gravity;
    const disc = this.vy * this.vy + 2 * g * Math.max(0, this.y);
    return (this.vy + Math.sqrt(Math.max(0, disc))) / g;
  }

  private crash(cause: Entity['kind'] | 'offroad', ev: SimEvent[]): void {
    if (this.invulnMs > 0) return;
    this.crashes++;
    this.speed *= CRASH_SPEED_KEEP;
    this.stunMs = CRASH_STUN_MS;
    this.invulnMs = CRASH_INVULN_MS;
    this.y = 0;
    this.vy = 0;
    this.grounded = true;
    this.trickActive = false;
    this.trickPhase = 0;
    this.breakCombo(ev);
    this.pushHighlight('crash', this.timeMs, this.crashes);
    ev.push({ type: 'crash', cause });
    if (this.crashes >= this.lives) {
      this.dead = true;
      ev.push({ type: 'dead' });
    }
  }

  private pushHighlight(kind: HighlightMoment['kind'], t: number, value = 0): void {
    if (this.highlights.length >= 40) return;
    this.highlights.push({ t, kind, value, theme: this.theme });
  }

  summary(mode: RunSummary['mode'], rules: number): RunSummary {
    return {
      mode,
      theme: this.theme,
      seed: this.track.seed,
      score: Math.round(this.score),
      distance: Math.round(this.distanceMetres),
      durationMs: Math.round(this.timeMs),
      topSpeed: Math.round(this.topSpeed),
      bestCombo: this.bestCombo,
      tricks: this.tricks,
      airTimeMs: Math.round(this.airMs),
      nearMisses: this.nearMisses,
      crashes: this.crashes,
      orbs: this.orbs,
      gears: this.gears,
      finished: this.finished,
      rules,
    };
  }
}

export function nearestLaneX(x: number): number {
  let best: number = LANE_X[0]!;
  let bestD = Infinity;
  for (const l of LANE_X) {
    const d = Math.abs(l - x);
    if (d < bestD) {
      bestD = d;
      best = l;
    }
  }
  return best;
}

export function livesForMode(mode: RunSummary['mode']): number {
  return mode === 'endless' || mode === 'daily' ? MAX_CRASHES_ENDLESS : Infinity;
}

/** Height the camera rides at, so the renderer and physics never disagree. */
export const CAMERA_Y = CAMERA_HEIGHT;
